'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "transitionStatus"];
import { ReactStore } from "../../../internal/@base-ui/utils/store";
import { generateId } from "../../../internal/@base-ui/utils/generateId";
import { ownerDocument } from "../../../internal/@base-ui/utils/owner";
import { Timeout } from "../../../internal/@base-ui/utils/useTimeout";
import { resolvePromiseOptions } from "./utils/resolvePromiseOptions.js";
import { activeElement, contains, getTarget } from "../floating-ui-react/utils.js";
import { isFocusVisible } from "./utils/focusVisible.js";

/**
 * A toast once it lives in the store. `addToast` is the only way in and it always
 * assigns `updateKey`, so unlike the public `ToastObject` it is never missing.
 */

function createToastMetadata(toasts) {
  const metadata = new Map();
  let visibleIndex = 0;
  let offsetY = 0;
  toasts.forEach((toast, toastIndex) => {
    const isEnding = toast.transitionStatus === 'ending';
    metadata.set(toast.id, {
      value: toast,
      domIndex: toastIndex,
      visibleIndex: isEnding ? -1 : visibleIndex,
      offsetY
    });
    offsetY += toast.height || 0;
    if (!isEnding) {
      visibleIndex += 1;
    }
  });
  return metadata;
}

// Marks the active (non-ending) toasts beyond `limit` as limited. Callers pass
// toasts in newest-first order, so the newest `limit` toasts stay visible and
// the rest are flagged. Returns the same toast reference when its `limited`
// flag is unchanged to avoid unnecessary re-renders.
function applyLimited(toasts, limit) {
  let activeIndex = 0;
  return toasts.map(toast => {
    if (toast.transitionStatus === 'ending') {
      return toast;
    }
    const limited = activeIndex >= limit;
    activeIndex += 1;
    return toast.limited === limited ? toast : Object.assign({}, toast, {
      limited
    });
  });
}
export const selectors = {
  toasts: state => state.toasts,
  isEmpty: state => state.toasts.length === 0,
  toast: (state, id) => {
    var _state$toastMetadata$;
    return (_state$toastMetadata$ = state.toastMetadata.get(id)) === null || _state$toastMetadata$ === void 0 ? void 0 : _state$toastMetadata$.value;
  },
  toastIndex: (state, id) => {
    var _state$toastMetadata$2, _state$toastMetadata$3;
    return (_state$toastMetadata$2 = (_state$toastMetadata$3 = state.toastMetadata.get(id)) === null || _state$toastMetadata$3 === void 0 ? void 0 : _state$toastMetadata$3.domIndex) !== null && _state$toastMetadata$2 !== void 0 ? _state$toastMetadata$2 : -1;
  },
  toastOffsetY: (state, id) => {
    var _state$toastMetadata$4, _state$toastMetadata$5;
    return (_state$toastMetadata$4 = (_state$toastMetadata$5 = state.toastMetadata.get(id)) === null || _state$toastMetadata$5 === void 0 ? void 0 : _state$toastMetadata$5.offsetY) !== null && _state$toastMetadata$4 !== void 0 ? _state$toastMetadata$4 : 0;
  },
  toastVisibleIndex: (state, id) => {
    var _state$toastMetadata$6, _state$toastMetadata$7;
    return (_state$toastMetadata$6 = (_state$toastMetadata$7 = state.toastMetadata.get(id)) === null || _state$toastMetadata$7 === void 0 ? void 0 : _state$toastMetadata$7.visibleIndex) !== null && _state$toastMetadata$6 !== void 0 ? _state$toastMetadata$6 : -1;
  },
  focused: state => state.focused,
  expanded: state => state.hovering || state.focused,
  expandedOrOutOfFocus: state => state.hovering || state.focused || !state.isWindowFocused,
  prevFocusElement: state => state.prevFocusElement
};
export class ToastStore extends ReactStore {
  constructor(initialState) {
    super(Object.assign({}, initialState, {
      toastMetadata: createToastMetadata(initialState.toasts)
    }), {}, selectors);
    this.timers = new Map();
    this.areTimersPaused = false;
    this.setViewport = viewport => {
      this.set('viewport', viewport);
    };
    this.disposeEffect = () => {
      return () => {
        this.timers.forEach(timer => {
          var _timer$timeout;
          (_timer$timeout = timer.timeout) === null || _timer$timeout === void 0 || _timer$timeout.clear();
        });
        this.timers.clear();
      };
    };
    this.addToast = toast => {
      var _toastToAdd$timeout;
      const {
        timeout,
        limit
      } = this.state;
      const id = toast.id || generateId('toast');
      if (toast.id) {
        const existingToast = selectors.toast(this.state, toast.id);
        if (existingToast) {
          if (existingToast.transitionStatus === 'ending') {
            this.removeToast(toast.id, true);
          } else {
            const updates = _objectWithoutPropertiesLoose(toast, _excluded);
            this.updateToastInternal(toast.id, updates, true, true);
            return toast.id;
          }
        }
      }
      const toastToAdd = Object.assign({}, toast, {
        id,
        updateKey: 0,
        transitionStatus: 'starting'
      });
      const updatedToasts = [toastToAdd, ...this.state.toasts];
      this.setToasts(applyLimited(updatedToasts, limit));
      const duration = (_toastToAdd$timeout = toastToAdd.timeout) !== null && _toastToAdd$timeout !== void 0 ? _toastToAdd$timeout : timeout;
      if (toastToAdd.type !== 'loading' && duration > 0) {
        this.scheduleTimer(id, duration, () => this.closeToast(id));
      }
      if (selectors.expandedOrOutOfFocus(this.state)) {
        this.pauseTimers();
      }
      return id;
    };
    this.updateToast = (id, updates) => {
      const prevToast = selectors.toast(this.state, id);
      // Never run the updater for an update the store is going to ignore.
      if (!prevToast || prevToast.transitionStatus === 'ending') {
        return;
      }

      // The updater may have called back into the store, so the internal update
      // reads the current state again.
      this.updateToastInternal(id, typeof updates === 'function' ? updates(prevToast) : updates, false, true);
    };
    this.updateToastInternal = (id, updates, resetTimer = false, markUpdated = false) => {
      var _nextToast$timeout, _prevToast$timeout;
      const {
        timeout,
        toasts
      } = this.state;
      const prevToast = selectors.toast(this.state, id);
      if (!prevToast) {
        return;
      }

      // Ignore updates for toasts that are already closing.
      // This prevents races where async updates (e.g. promise success/error)
      // can block a dismissal from completing.
      if (prevToast.transitionStatus === 'ending') {
        return;
      }
      const nextToast = Object.assign({}, prevToast, updates, markUpdated && {
        updateKey: prevToast.updateKey + 1
      });
      this.setToasts(toasts.map(toast => toast.id === id ? nextToast : toast));
      const nextTimeout = (_nextToast$timeout = nextToast.timeout) !== null && _nextToast$timeout !== void 0 ? _nextToast$timeout : timeout;
      const prevTimeout = (_prevToast$timeout = prevToast.timeout) !== null && _prevToast$timeout !== void 0 ? _prevToast$timeout : timeout;
      const timeoutUpdated = Object.hasOwn(updates, 'timeout');
      const shouldHaveTimer = nextToast.transitionStatus !== 'ending' && nextToast.type !== 'loading' && nextTimeout > 0;
      const hasTimer = this.timers.has(id);
      const timeoutChanged = prevTimeout !== nextTimeout;
      const wasLoading = prevToast.type === 'loading';
      if (!shouldHaveTimer && hasTimer) {
        this.clearTimer(id);
        return;
      }

      // Schedule or reschedule timer if needed
      if (shouldHaveTimer && (!hasTimer || timeoutChanged || timeoutUpdated || wasLoading || resetTimer)) {
        this.clearTimer(id);
        this.scheduleTimer(id, nextTimeout, () => this.closeToast(id));
        if (selectors.expandedOrOutOfFocus(this.state)) {
          this.pauseTimers();
        }
      }
    };
    this.closeToast = toastId => {
      const closeAll = toastId === undefined;
      const {
        limit,
        toasts
      } = this.state;
      let toastsToClose;
      if (closeAll) {
        toastsToClose = toasts;
        this.clearTimers();
      } else {
        const toast = selectors.toast(this.state, toastId);
        if (!toast) {
          return;
        }
        toastsToClose = [toast];
        this.clearTimer(toastId);
      }
      const endingToasts = toasts.map(item => closeAll || item.id === toastId ? Object.assign({}, item, {
        transitionStatus: 'ending',
        height: 0
      }) : item);
      const newToasts = applyLimited(endingToasts, limit);
      this.setToasts(newToasts, !newToasts.some(toast => toast.transitionStatus !== 'ending'));
      toastsToClose.forEach(toast => {
        if (toast.transitionStatus !== 'ending') {
          var _toast$onClose;
          (_toast$onClose = toast.onClose) === null || _toast$onClose === void 0 || _toast$onClose.call(toast);
        }
      });
      this.handleFocusManagement(toastId);
    };
    this.promiseToast = (promiseValue, options) => {
      // Create a loading toast (which does not auto-dismiss).
      const loadingOptions = resolvePromiseOptions(options.loading);
      const id = this.addToast(Object.assign({}, loadingOptions, {
        type: 'loading'
      }));
      const handledPromise = promiseValue.then(result => {
        const successOptions = resolvePromiseOptions(options.success, result);
        this.updateToast(id, Object.assign({}, successOptions, {
          type: 'success',
          timeout: successOptions.timeout
        }));
        return result;
      }).catch(error => {
        const errorOptions = resolvePromiseOptions(options.error, error);
        this.updateToast(id, Object.assign({}, errorOptions, {
          type: 'error',
          timeout: errorOptions.timeout
        }));
        return Promise.reject(error);
      });

      // Private API used exclusively by `Manager` to handoff the promise
      // back to the manager after it's handled here.
      if ({}.hasOwnProperty.call(options, 'setPromise')) {
        options.setPromise(handledPromise);
      }
      return handledPromise;
    };
    this.handleDocumentPointerDown = event => {
      if (event.pointerType !== 'touch') {
        return;
      }
      const target = getTarget(event);
      if (contains(this.state.viewport, target)) {
        return;
      }

      // This is explicit touch activity outside the viewport, so the paused
      // interaction state should end even if the window focus state is unchanged.
      this.resumeTimers();
      this.update({
        hovering: false,
        focused: false
      });
    };
  }
  syncProviderProps(timeout, limit) {
    const limitChanged = this.state.limit !== limit;
    if (this.state.timeout === timeout && !limitChanged) {
      return;
    }
    const updates = {
      timeout,
      limit
    };
    if (limitChanged) {
      const newToasts = applyLimited(this.state.toasts, limit);
      updates.toasts = newToasts;
      updates.toastMetadata = createToastMetadata(newToasts);
    }
    this.update(updates);
  }
  removeToast(toastId, skipOnRemove = false) {
    const index = selectors.toastIndex(this.state, toastId);
    if (index === -1) {
      return;
    }
    const toast = this.state.toasts[index];
    if (!skipOnRemove) {
      var _toast$onRemove;
      toast === null || toast === void 0 || (_toast$onRemove = toast.onRemove) === null || _toast$onRemove === void 0 || _toast$onRemove.call(toast);
    }
    const newToasts = [...this.state.toasts];
    newToasts.splice(index, 1);
    this.setToasts(newToasts);
  }
  pauseTimers() {
    if (this.areTimersPaused) {
      return;
    }
    this.areTimersPaused = true;
    this.timers.forEach(timer => {
      // Timers added while already paused have no running timeout, so their
      // `remaining` is still the full delay and must be left alone.
      if (timer.timeout) {
        timer.timeout.clear();
        // `start` is stamped on every resume, so subtracting from `remaining`
        // (rather than from the original delay) keeps repeated pause/resume
        // cycles from handing the toast extra time.
        timer.remaining = Math.max(timer.remaining - (Date.now() - timer.start), 0);
      }
    });
  }
  resumeTimers() {
    if (!this.areTimersPaused) {
      return;
    }
    this.areTimersPaused = false;
    this.timers.forEach((timer, id) => {
      var _timer$timeout2;
      timer.remaining = timer.remaining > 0 ? timer.remaining : timer.delay;
      (_timer$timeout2 = timer.timeout) !== null && _timer$timeout2 !== void 0 ? _timer$timeout2 : timer.timeout = Timeout.create();
      timer.timeout.start(timer.remaining, () => {
        this.handleTimerFired(id);
        timer.callback();
      });
      timer.start = Date.now();
    });
  }
  restoreFocusToPrevElement() {
    var _this$state$prevFocus;
    (_this$state$prevFocus = this.state.prevFocusElement) === null || _this$state$prevFocus === void 0 || _this$state$prevFocus.focus({
      preventScroll: true
    });
  }
  scheduleTimer(id, delay, callback) {
    const start = Date.now();
    const shouldStartActive = !selectors.expandedOrOutOfFocus(this.state);
    const currentTimeout = shouldStartActive ? Timeout.create() : undefined;
    currentTimeout === null || currentTimeout === void 0 || currentTimeout.start(delay, () => {
      this.handleTimerFired(id);
      callback();
    });
    this.timers.set(id, {
      timeout: currentTimeout,
      start,
      delay,
      remaining: delay,
      callback
    });
  }
  clearTimers() {
    this.timers.forEach(timer => {
      var _timer$timeout3;
      (_timer$timeout3 = timer.timeout) === null || _timer$timeout3 === void 0 || _timer$timeout3.clear();
    });
    this.timers.clear();
    this.areTimersPaused = false;
  }
  clearTimer(id) {
    var _timer$timeout4;
    const timer = this.timers.get(id);
    timer === null || timer === void 0 || (_timer$timeout4 = timer.timeout) === null || _timer$timeout4 === void 0 || _timer$timeout4.clear();
    this.timers.delete(id);
    this.resetPausedStateIfNoTimersRemain();
  }
  handleTimerFired(id) {
    this.timers.delete(id);
    this.resetPausedStateIfNoTimersRemain();
  }
  resetPausedStateIfNoTimersRemain() {
    if (this.timers.size === 0) {
      // No timers remain to keep paused; clear the flag so a fresh toast's
      // running timer can be paused again on hover/focus.
      this.areTimersPaused = false;
    }
  }
  setToasts(newToasts, clearInteraction = newToasts.length === 0) {
    const updates = {
      toasts: newToasts,
      toastMetadata: createToastMetadata(newToasts)
    };
    if (clearInteraction) {
      updates.hovering = false;
      updates.focused = false;
    }
    this.update(updates);
  }
  handleFocusManagement(toastId) {
    var _scan;
    const activeEl = activeElement(ownerDocument(this.state.viewport));
    if (!this.state.viewport || !contains(this.state.viewport, activeEl) || !isFocusVisible(activeEl)) {
      return;
    }
    if (toastId === undefined) {
      this.restoreFocusToPrevElement();
      return;
    }
    const toasts = selectors.toasts(this.state);
    const currentIndex = selectors.toastIndex(this.state, toastId);
    const scan = (from, step) => {
      for (let index = from; index >= 0 && index < toasts.length; index += step) {
        if (toasts[index].transitionStatus !== 'ending') {
          return toasts[index];
        }
      }
      return null;
    };

    // Try to find the next toast that isn't animating out, then fall back to the previous one.
    const nextToast = (_scan = scan(currentIndex + 1, 1)) !== null && _scan !== void 0 ? _scan : scan(currentIndex - 1, -1);
    if (nextToast) {
      var _nextToast$ref;
      (_nextToast$ref = nextToast.ref) === null || _nextToast$ref === void 0 || (_nextToast$ref = _nextToast$ref.current) === null || _nextToast$ref === void 0 || _nextToast$ref.focus();
    } else {
      this.restoreFocusToPrevElement();
    }
  }
}